<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import type { CaptchaResp, LoginInput } from '@yc/api-client'

import TraceIdAlert from './TraceIdAlert.vue'

const props = withDefaults(
  defineProps<{
    appName: string
    tagline: string
    color?: string
    captcha?: CaptchaResp | null
    submitting?: boolean
    error?: unknown
  }>(),
  { color: 'var(--yc-brand)', captcha: null, submitting: false },
)

const emit = defineEmits<{
  (e: 'submit', payload: LoginInput): void
  (e: 'refresh-captcha'): void
}>()

const form = reactive({ tenant_code: '', username: '', password: '', captcha_answer: '' })

const captchaRequired = computed(() => !!props.captcha)
const canSubmit = computed(
  () =>
    !!form.tenant_code.trim() &&
    !!form.username.trim() &&
    !!form.password &&
    (!captchaRequired.value || !!form.captcha_answer.trim()) &&
    !props.submitting,
)

/** 提交后立即清空父组件的旧错误 */
const dismissed = ref(false)
watch(
  () => props.error,
  () => {
    dismissed.value = false
  },
)

const errorText = computed(() => (dismissed.value ? '' : props.error))

// 后端验证码是一次性的（GETDEL），换图必须清空已输入的答案，避免把旧答案提交到新题上
watch(
  () => props.captcha?.captcha_id,
  () => {
    form.captcha_answer = ''
  },
)

function onSubmit(): void {
  if (!canSubmit.value) return
  dismissed.value = true
  emit('submit', {
    tenant_code: form.tenant_code.trim(),
    username: form.username.trim(),
    password: form.password,
    captcha_id: props.captcha?.captcha_id,
    captcha_answer: form.captcha_answer.trim() || undefined,
  })
}

function onCaptchaClick(): void {
  form.captcha_answer = ''
  emit('refresh-captcha')
}

defineExpose({ form, reset: () => Object.assign(form, { password: '', captcha_answer: '' }) })
</script>

<template>
  <div class="yc-split-login">
    <aside class="yc-split-login__brand" :style="{ background: props.color }">
      <div class="yc-split-login__brand-inner">
        <div class="yc-split-login__logo">云仓供应链平台</div>
        <h1 class="yc-split-login__title">{{ props.appName }}</h1>
        <p class="yc-split-login__tagline">{{ props.tagline }}</p>

        <svg
          class="yc-split-login__art"
          viewBox="0 0 320 160"
          fill="none"
          aria-hidden="true"
          role="presentation"
        >
          <path d="M10 130 H120 V70 H200 V20 H310" stroke="rgba(255,255,255,.55)" stroke-width="1.5" />
          <path d="M10 150 H170 V100 H250 V46 H310" stroke="rgba(255,255,255,.28)" stroke-width="1.5" />
          <rect x="106" y="56" width="28" height="28" stroke="rgba(255,255,255,.8)" stroke-width="1.5" />
          <rect x="186" y="86" width="28" height="28" stroke="rgba(255,255,255,.8)" stroke-width="1.5" />
          <rect x="236" y="32" width="28" height="28" fill="rgba(255,255,255,.9)" />
          <circle cx="120" cy="130" r="4" fill="rgba(255,255,255,.9)" />
          <circle cx="170" cy="150" r="4" fill="rgba(255,255,255,.5)" />
        </svg>

        <ul class="yc-split-login__points">
          <li>统一账号，一次登录通达各业务系统</li>
          <li>按角色与货主隔离数据范围</li>
          <li>关键操作全程留痕可追溯</li>
        </ul>
      </div>
    </aside>

    <main class="yc-split-login__panel">
      <div class="yc-split-login__form">
        <h2 class="yc-split-login__form-title">登录</h2>
        <p class="yc-split-login__form-hint">使用平台统一账号，登录后各系统通用</p>

        <div v-if="errorText" class="yc-split-login__alert" role="alert">
          <TraceIdAlert :error="errorText" />
        </div>

        <el-form label-position="top" @submit.prevent="onSubmit">
          <el-form-item label="企业编码">
            <el-input
              v-model="form.tenant_code"
              placeholder="如 yc"
              autocomplete="organization"
              name="tenant_code"
            />
          </el-form-item>

          <el-form-item label="用户名">
            <el-input
              v-model="form.username"
              placeholder="请输入用户名"
              autocomplete="username"
              name="username"
            />
          </el-form-item>

          <el-form-item label="密码">
            <el-input
              v-model="form.password"
              type="password"
              placeholder="请输入密码"
              autocomplete="current-password"
              name="password"
              show-password
              @keyup.enter="onSubmit"
            />
          </el-form-item>

          <el-form-item v-if="captchaRequired" label="图形验证码">
            <div class="yc-split-login__captcha">
              <el-input
                v-model="form.captcha_answer"
                placeholder="请输入验证码"
                name="captcha_answer"
                @keyup.enter="onSubmit"
              />
              <button
                type="button"
                class="yc-split-login__captcha-img"
                title="点击换一张"
                @click="onCaptchaClick"
              >
                <img v-if="props.captcha?.image" :src="props.captcha.image" alt="图形验证码" />
                <span v-else>获取中</span>
              </button>
            </div>
          </el-form-item>

          <el-button
            class="yc-split-login__submit"
            type="primary"
            native-type="submit"
            :loading="props.submitting"
            :disabled="!canSubmit"
          >
            登录
          </el-button>
        </el-form>
      </div>
    </main>
  </div>
</template>

<style scoped lang="scss">
.yc-split-login {
  display: grid;
  grid-template-columns: minmax(360px, 44%) 1fr;
  min-height: 100vh;

  &__brand {
    display: flex;
    align-items: center;
    color: var(--yc-text-inverse);
    padding: var(--yc-space-8);
  }

  &__brand-inner {
    max-width: 420px;
  }

  &__logo {
    font-size: var(--yc-font-lg);
    font-weight: 600;
    letter-spacing: 0.08em;
    opacity: 0.9;
  }

  &__title {
    margin: var(--yc-space-6) 0 var(--yc-space-3);
    font-size: var(--yc-font-3xl);
    line-height: 1.25;
    font-weight: 600;
  }

  &__tagline {
    margin: 0;
    font-size: var(--yc-font-lg);
    opacity: 0.86;
    line-height: 1.6;
  }

  &__art {
    width: 100%;
    max-width: 320px;
    margin: var(--yc-space-8) 0;
  }

  &__points {
    margin: 0;
    padding-left: 18px;
    font-size: var(--yc-font-sm);
    line-height: 2;
    opacity: 0.82;
  }

  &__panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--yc-space-8);
    background: var(--yc-card);
  }

  &__form {
    width: 100%;
    max-width: 360px;
  }

  &__form-title {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__form-hint {
    margin: var(--yc-space-2) 0 var(--yc-space-6);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__alert {
    margin-bottom: var(--yc-space-4);
    padding: var(--yc-space-2) var(--yc-space-3);
    color: var(--yc-danger);
    background: var(--yc-danger-subtle);
    border: 1px solid #fecaca;
    border-radius: var(--yc-radius);
    font-size: var(--yc-font-sm);
    line-height: 1.6;
  }

  &__captcha {
    display: flex;
    gap: var(--yc-space-2);
    width: 100%;
  }

  &__captcha-img {
    flex: 0 0 120px;
    height: 32px;
    padding: 0;
    overflow: hidden;
    cursor: pointer;
    background: var(--yc-sunken);
    border: 1px solid var(--yc-border);
    border-radius: var(--yc-radius);
    color: var(--yc-text-muted);
    font-size: var(--yc-font-xs);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__submit {
    width: 100%;
    margin-top: var(--yc-space-2);
  }
}

@media (width <= 900px) {
  .yc-split-login {
    grid-template-columns: 1fr;

    &__brand {
      padding: var(--yc-space-6);
    }

    &__art,
    &__points {
      display: none;
    }
  }
}
</style>